import { useEffect } from 'react';
import { getSession } from '@mysten/enoki';
import type { WalletWithRequiredFeatures } from '@mysten/wallet-standard';

import { zkloginRepository } from '@/repositories/zklogin-repository';
import { getWalletUniqueIdentifier } from '../utils/wallet.util';
import { useZkLoginWallet } from './useZkLoginWallet';

type ZkLoginJwtSyncState = {
	pendingWalletId: string | null;
	promise: Promise<void> | null;
	syncedWalletId: string | null;
};

const defaultState: ZkLoginJwtSyncState = {
	pendingWalletId: null,
	promise: null,
	syncedWalletId: null,
};

const syncState: ZkLoginJwtSyncState = { ...defaultState };

export async function ensureZkLoginJwtSynced(wallet: WalletWithRequiredFeatures | null): Promise<void> {
	if (!wallet) {
		return;
	}

	const walletIdentifier = getWalletUniqueIdentifier(wallet);
	if (!walletIdentifier) {
		return;
	}

	if (syncState.syncedWalletId === walletIdentifier) {
		return;
	}

	if (syncState.promise && syncState.pendingWalletId === walletIdentifier) {
		await syncState.promise;
		return;
	}

	const promise = (async () => {
		const session = await getSession(wallet);
		const jwt = session?.jwt;
		if (!jwt) {
			throw new Error('No JWT found in session');
		}

		await zkloginRepository.sendJwt(jwt);
		syncState.syncedWalletId = walletIdentifier;
	})();

	syncState.pendingWalletId = walletIdentifier;
	syncState.promise = promise;

	try {
		await promise;
	} finally {
		if (syncState.promise === promise) {
			syncState.pendingWalletId = null;
			syncState.promise = null;
		}
	}
}

export function resetZkLoginJwtSyncState(walletIdentifier?: string | null) {
	if (!walletIdentifier || syncState.syncedWalletId === walletIdentifier) {
		syncState.syncedWalletId = null;
	}
	if (!walletIdentifier || syncState.pendingWalletId === walletIdentifier) {
		syncState.pendingWalletId = null;
		syncState.promise = null;
	}
}

export function useZkLoginJwtSync(enabled: boolean = true) {
	const { currentWallet, authToken } = useZkLoginWallet();

	useEffect(() => {
		if (!enabled) {
			return;
		}
		if (!currentWallet) {
			resetZkLoginJwtSyncState();
			return;
		}

		if (authToken) {
			return;
		}

		let cancelled = false;
		const run = async () => {
			try {
				await ensureZkLoginJwtSynced(currentWallet);
			} catch (error) {
				if (!cancelled) {
					console.warn('Failed to sync zkLogin JWT with API', error);
				}
			}
		};

		void run();

		return () => {
			cancelled = true;
		};
	}, [authToken, currentWallet, enabled]);
}
